import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Brain, MessageCircle, Shield, Heart, Sparkles, 
  ArrowRight, CheckCircle, Lock 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AITherappiSupportProps {
  context: 'habit-change' | 'wellness-journey' | 'mental-health';
  detectedPatterns?: string[];
  hasAccess?: boolean;
  onUpgrade?: () => void;
}

export default function AITherappiSupport({ 
  context, 
  detectedPatterns = [],
  hasAccess = false,
  onUpgrade 
}: AITherappiSupportProps) {
  const navigate = useNavigate();

  const getContextualContent = () => {
    switch (context) {
      case 'habit-change':
        return {
          title: 'Need Support Changing Habits?',
          description: 'AI Therappi provides trauma-informed, evidence-based guidance for sustainable behaviour change',
          benefits: [
            'Identify underlying needs behind habits',
            'Navigate alcohol, eating, or substance concerns compassionately',
            'Build motivation and overcome resistance',
            'Process emotions driving behaviours',
            'Create personalised action plans'
          ],
          cta: 'Get AI Support for Habit Change'
        };
      case 'wellness-journey':
        return {
          title: 'Accelerate Your Wellness Journey',
          description: 'Work with AI Therappi to optimise sleep, nutrition, exercise, and mental wellbeing holistically',
          benefits: [
            'Personalised wellness coaching based on your data',
            'Integrate insights from check-ins and WHOOP data',
            'Evidence-based strategies for lasting change',
            'Accountability and motivation support',
            'Progress reflection and pattern recognition'
          ],
          cta: 'Start AI-Guided Wellness Coaching'
        };
      case 'mental-health':
        return {
          title: 'Therapeutic Support Available',
          description: 'AI Therappi offers compassionate, trauma-informed support for anxiety, depression, grief, PTSD, OCD, and more',
          benefits: [
            'CBT, DBT, and psychotherapy techniques',
            'Trauma-informed and shame-free approach',
            'Support for anxiety, depression, grief, PTSD, C-PTSD',
            'Tools for OCD, panic, anger, and emotional regulation',
            'Never diagnoses - focuses on therapeutic support'
          ],
          cta: 'Access Therapeutic Support'
        };
    }
  };

  const content = getContextualContent();

  if (hasAccess) {
    return (
      <Card className="p-6 bg-gradient-to-br from-primary/10 via-accent/5 to-background border-primary/30">
        <div className="space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Brain className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-bold">AI Therappi Active</h3>
                <p className="text-sm text-muted-foreground">Ready to support your journey</p>
              </div>
            </div>
            <Badge className="bg-accent/20 text-accent border-accent/30">
              <CheckCircle className="w-3 h-3 mr-1" />
              Subscribed
            </Badge>
          </div>

          {detectedPatterns.length > 0 && (
            <div className="p-3 bg-background/60 rounded-lg border border-border/50">
              <p className="text-xs text-muted-foreground mb-2">Based on your patterns, AI Therappi can help with:</p>
              <ul className="space-y-1">
                {detectedPatterns.map((pattern, idx) => (
                  <li key={idx} className="text-sm flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-accent flex-shrink-0" />
                    {pattern}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button 
            onClick={() => navigate('/therapy')}
            className="w-full gap-2"
            variant="default"
          >
            <MessageCircle className="w-4 h-4" />
            Start Conversation with AI Therappi
            <ArrowRight className="w-4 h-4" />
          </Button>

          <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg border border-primary/10">
            <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Your conversations are confidential, encrypted, and guided by trauma-informed principles. 
              AI Therappi provides therapeutic support but doesn't diagnose or replace professional care.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-accent/5 via-background to-primary/5 border-accent/20 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-accent/10 rounded-full blur-3xl" />
      
      <div className="relative space-y-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-accent/10">
              <Brain className="w-7 h-7 text-accent" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-xl font-bold">{content.title}</h3>
                <Lock className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">{content.description}</p>
            </div>
          </div>
        </div>

        {detectedPatterns.length > 0 && (
          <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold">Detected Patterns AI Therappi Can Address</span>
            </div>
            <ul className="space-y-1.5">
              {detectedPatterns.map((pattern, idx) => (
                <li key={idx} className="text-sm text-foreground flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  {pattern}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-accent" />
            What AI Therappi Offers
          </h4>
          <ul className="space-y-2">
            {content.benefits.map((benefit, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        <div className="p-4 bg-background/60 rounded-lg border border-border/50">
          <div className="flex items-start gap-2 mb-3">
            <Shield className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium mb-1">Trauma-Informed & Ethical</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                AI Therappi is trained in evidence-based approaches for anxiety, depression, PTSD, C-PTSD, OCD, 
                panic disorder, grief, anger management, eating difficulties, and substance concerns. 
                It provides compassionate therapeutic support while adhering to ethical boundaries 
                (no diagnosis or treatment - only guidance and support).
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Button 
            onClick={onUpgrade || (() => navigate('/subscription'))}
            className="w-full gap-2"
            size="lg"
          >
            <Brain className="w-5 h-5" />
            {content.cta}
            <ArrowRight className="w-5 h-5" />
          </Button>
          
          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              Available as standalone (£12.99/mo) or add-on to existing subscriptions (£9.99/mo)
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
}
